import {
  Component,
  computed,
  signal,
  effect,
  inject,
  input,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SSLCertificateInfo } from '../../../models/documentMetadata';
import moment from 'moment';

@Component({
  selector: 'app-metadata-ssl-certificate',
  templateUrl: './metadata-ssl-certificate.component.html',
  styleUrls: ['../crawled-document-metadata.component.scss'],
})
export class MetadataSslCertificateComponent {
  readonly cert = input.required<SSLCertificateInfo>();
  private readonly translate = inject(TranslateService);

  readonly currentTime = signal(Date.now());

  constructor() {
    effect((onCleanup) => {
      const interval = setInterval(() => {
        this.currentTime.set(Date.now());
      }, 60_000);

      onCleanup(() => clearInterval(interval));
    });
  }

  // region computed
  readonly readableFromDate = computed(() =>
    this.toReadableDate(this.cert().validity.from)
  );

  readonly readableToDate = computed(() =>
    this.toReadableDate(this.cert().validity.to)
  );

  readonly isoFromDate = computed(() =>
    this.toISODate(this.cert().validity.from)
  );

  readonly isoToDate = computed(() =>
    this.toISODate(this.cert().validity.to)
  );

  readonly isValid = computed(() =>
    this.isCertificateValid(this.cert().validity.from, this.cert().validity.to)
  );

  readonly parsedSans = computed(() =>
    this.parseSanDomains(this.cert().subject)
  );

  // endregion

  private parseSanDomains(subject: string): string[] {
    return subject
      .split(',')
      .map(s => s.trim().replace(/^DNS:/, ''))
      .filter(Boolean);
  }

  private isCertificateValid(from: string, to: string): boolean {
    const now = this.currentTime();
    return moment(from).isSameOrBefore(now) && moment(to).isSameOrAfter(now);
  }

  private toReadableDate(dateStr: string): string {
    if (!dateStr) return this.translate.instant('ERRORS.NOT_AVAILABLE');
    const date = moment(dateStr);
    if (!date.isValid()) return this.translate.instant('ERRORS.INVALID_DATE');
    return Math.abs(date.diff(moment(this.currentTime()), 'days')) <= 7
      ? date.fromNow()
      : date.format('LLLL');
  }

  private toISODate(dateStr: string): string {
    return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
  }
}
